/**
 * useOtherUserProfile — loads another member's profile by GUID.
 *
 * GET /UserProfile/GetOtherUserProfile answers 200 with `isAccessible: false`
 * (or `success: false`) when the owner's privacy setting blocks the viewer, so
 * a restriction is not an HTTP error and has to be normalised here rather than
 * in a screen.
 */
import { useCallback, useEffect, useState } from 'react';
import profileService from '../api/services/profileService';
import { mapApiProfileToProfile } from '../utils/userProfileMapper';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import { getProfileRestrictionReason } from '../utils/profileVisibility';
import { normalizeUserGuid } from '../utils/navigateToUserProfile';
import type { ProfileRestrictionReason } from '../utils/profileVisibility';
import type { ApiError } from '../types/api';
import type { Profile } from '../types/profile';

export interface UseOtherUserProfileResult {
  profile: Profile | null;
  loading: boolean;
  /** Transport/server failure message. Empty when the load succeeded. */
  error: string;
  /** Set when the profile exists but the viewer may not see it. */
  restriction: ProfileRestrictionReason | null;
  refetch: () => void;
}

export const useOtherUserProfile = (
  profileUserGuid: string | null | undefined,
): UseOtherUserProfileResult => {
  const guid = normalizeUserGuid(profileUserGuid);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(Boolean(guid));
  const [error, setError] = useState('');
  const [restriction, setRestriction] = useState<ProfileRestrictionReason | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const refetch = useCallback(() => setReloadToken(token => token + 1), []);

  useEffect(() => {
    if (!guid) {
      setProfile(null);
      setLoading(false);
      setRestriction('unavailable');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setRestriction(null);

    profileService
      .getOtherUserProfile(guid)
      .then(response => {
        if (cancelled) return;
        const body = response?.data;

        if (!body || body.success === false || body.isAccessible === false) {
          setProfile(null);
          // The body may still carry profileViewPrivacy + isBuddy even when
          // inaccessible, so we can distinguish "Buddies only" from "Private".
          const reason = getProfileRestrictionReason(
            body?.profileViewPrivacy,
            { isBuddy: body?.isBuddy },
          );
          setRestriction(reason ?? 'unavailable');
          setLoading(false);
          return;
        }

        const mapped = mapApiProfileToProfile(body);
        // The server is the authority, but re-running the rule keeps the UI
        // honest if a payload ever arrives without the accessibility flag.
        setRestriction(
          getProfileRestrictionReason(mapped.profileViewPrivacy, {
            isBuddy: mapped.isBuddy,
          }),
        );
        setProfile(mapped);
        setLoading(false);
      })
      .catch(caught => {
        if (cancelled) return;
        setProfile(null);
        const status = (caught as ApiError & { response?: { status?: number } })
          ?.response?.status;
        if (status === 404) {
          setRestriction('unavailable');
        } else {
          setError(getApiErrorMessage(caught as ApiError));
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [guid, reloadToken]);

  return { profile, loading, error, restriction, refetch };
};

export default useOtherUserProfile;
